import { useId } from 'react'

/**
 * The tile-field artwork shared by the site's card thumbnails: a hairline grid
 * with a sparse mosaic laid over it, each tile a random window into a gradient
 * artwork, and the Euclideum mark set directly on the pattern — no clearing,
 * no mask, no backing shape.
 *
 * ProductThumb draws it on the brand half of a split card; BlogThumb draws it
 * across a whole band. Only the composition and the mark's placement differ,
 * so everything below the JSX is geometry both can share.
 *
 * Fields are authored as ASCII maps over an 11×7 grid: `1`–`3` drop a tile,
 * `+` and `/` drop a large or small diamond, `.` leaves the cell open. Runs of
 * the same digit merge into one wide tile.
 */

export const COLS = 11
export const ROWS = 7
export const CELL = 44
export const W = COLS * CELL // 484
export const H = ROWS * CELL // 308

/** The gradient artwork's coordinate space (1600px webp, 5600×3550 source). */
export const IMG_W = 1600
export const IMG_H = 1015

/** The mark's own 311×504 box. */
export const MARK_RATIO = 311 / 504

export interface Tile {
  x: number
  y: number
  s: number
  w: number
  turned?: boolean
}

export interface Composition {
  /** ROWS strings of COLS characters. */
  map: readonly string[]
}

/** A rectangle the mosaic has to leave alone, in viewBox units. */
export interface Zone {
  x0: number
  x1: number
  y0: number
  y1: number
}

export function buildTiles({ map }: Composition): Tile[] {
  const tiles: Tile[] = []
  map.forEach((line, r) => {
    let c = 0
    while (c < COLS) {
      const ch = line[c]
      if (ch === '/' || ch === '+') {
        // Diamonds sit centred in their cell, kept under cell size so the
        // rotation never bleeds into a neighbour.
        const s = ch === '+' ? CELL * 0.84 : CELL * 0.38
        tiles.push({
          x: (c + 0.5) * CELL - s / 2,
          y: (r + 0.5) * CELL - s / 2,
          s,
          w: s,
          turned: true,
        })
      } else if (ch && ch >= '1' && ch <= '3') {
        let w = 1
        while (c + w < COLS && line[c + w] === ch) w += 1
        tiles.push({ x: c * CELL, y: r * CELL, s: CELL, w: w * CELL })
        c += w
        continue
      }
      c += 1
    }
  })
  return tiles
}

/**
 * Filter for the cells the mark occupies: any tile that so much as clips into
 * that footprint is dropped at build time, so no pattern ever sits behind or
 * under the logo. Rotated diamonds check with their inflated bounding box.
 */
export function clearOf(zone: Zone) {
  return (t: Tile): boolean => {
    const pad = t.turned ? (t.s * (Math.SQRT2 - 1)) / 2 : 0
    const x0 = t.x - pad
    const x1 = t.x + t.w + pad
    const y0 = t.y - pad
    const y1 = t.y + t.s + pad
    return x1 <= zone.x0 || x0 >= zone.x1 || y1 <= zone.y0 || y0 >= zone.y1
  }
}

/**
 * One of the four axis-aligned orientations of a map — bit 0 mirrors left to
 * right, bit 1 top to bottom. A field carries rhythm rather than a picture, so
 * a mirror reads as a different composition rather than the same one flipped,
 * which is a cheap way to widen the pool without authoring more maps.
 *
 * The mark's footprint is symmetric about both axes of the grid (cols 4–6 of
 * 0–10, rows 2–4 of 0–6), so a composition that clears it upright clears it in
 * every orientation.
 */
export function orient(
  map: readonly string[],
  flip: number
): readonly string[] {
  const rows =
    flip & 1 ? map.map((row) => [...row].reverse().join('')) : [...map]
  return flip & 2 ? rows.reverse() : rows
}

/**
 * Build a composition and drop whatever falls under the mark.
 *
 * `clearOf` deletes silently, which is how compositions quietly lose an
 * authored accent — the field still renders, just poorer, and nothing says so.
 * In development this reports what it took, naming the composition so the map
 * can be corrected. A `+` needs a cell that clears the footprint with room for
 * its rotated bounding box; for a centred zone that means cols 0–2 or 8–10 at
 * any row, or any column in the top and bottom rows.
 */
export function buildField(
  composition: Composition,
  zone: Zone,
  label: string
): Tile[] {
  const authored = buildTiles(composition)
  const kept = authored.filter(clearOf(zone))

  if (
    process.env.NODE_ENV !== 'production' &&
    kept.length !== authored.length
  ) {
    console.warn(
      `[pattern-field] ${label}: ${authored.length - kept.length} mark(s) fall ` +
        `inside the logo's footprint and were dropped.`
    )
  }

  return kept
}

export const GRID = [
  ...Array.from({ length: COLS - 1 }, (_, i) => `M${(i + 1) * CELL} 0V${H}`),
  ...Array.from({ length: ROWS - 1 }, (_, i) => `M0 ${(i + 1) * CELL}H${W}`),
].join('')

// Small fast PRNG — one seed drives a whole mosaic, so a single state value
// re-rolls every tile at once.
export function mulberry32(a: number) {
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** FNV-1a, so a slug can seed a field that stays put across renders. */
export function hashSeed(value: string) {
  let h = 2166136261
  for (let i = 0; i < value.length; i += 1) {
    h ^= value.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

export interface Crop {
  vx: number
  vy: number
  vw: number
  vh: number
}

/**
 * A tile's window into the artwork. Zoom 2–4.5×: enough of the artwork per
 * tile to read as a soft field, small enough that neighbouring tiles land on
 * visibly different zones. Consumes three draws, so the same `rand` walked in
 * the same order reproduces a whole mosaic.
 */
export function cropFor(rand: () => number, t: Tile): Crop {
  const k = 2 + rand() * 2.5
  const vw = Math.min(t.w * k, IMG_W)
  const vh = Math.min(t.s * k, IMG_H)
  return {
    vx: rand() * (IMG_W - vw),
    vy: rand() * (IMG_H - vh),
    vw,
    vh,
  }
}

/**
 * When a field animates in, tiles do not fire in DOM order — the map is
 * scattered, so index order reads as random flicker. Delay comes from the
 * tile's horizontal position instead: one clean left-to-right wipe, tiles in
 * the same column landing together. (A diagonal position-sum sweep was tried
 * first and still read as random on a sparse map.)
 */
export function tileDelay(t: Tile, span = 200) {
  const cx = (t.x + t.w / 2) / W
  return Math.round(cx * span)
}

/** Each tile as a nested <svg> whose viewBox is that tile's crop. */
export function TileMosaic({
  tiles,
  crops,
  art,
  animate = false,
}: {
  tiles: Tile[]
  crops: Crop[]
  art: string
  /**
   * Tag each tile for the staggered entry (see `.pattern-anim` in
   * globals.css). Opt-in, so a field that just sits on a page stays static.
   */
  animate?: boolean
}) {
  return (
    <g className="opacity-75 dark:opacity-[0.45]">
      {tiles.map((t, i) => {
        const c = crops[i]!
        const tile = (
          <svg
            x={t.x}
            y={t.y}
            width={t.w}
            height={t.s}
            viewBox={`${c.vx} ${c.vy} ${c.vw} ${c.vh}`}
            preserveAspectRatio="xMidYMid slice"
          >
            <image
              href={art}
              width={IMG_W}
              height={IMG_H}
              preserveAspectRatio="none"
            />
          </svg>
        )

        // Rotation lives on an inner group so the outer one keeps its
        // transform free for the entry animation's scale.
        const shape = t.turned ? (
          <g transform={`rotate(45 ${t.x + t.w / 2} ${t.y + t.s / 2})`}>
            {tile}
          </g>
        ) : (
          tile
        )

        return (
          <g
            key={i}
            className={animate ? 'pattern-tile' : undefined}
            // The card's own start offset (--pattern-delay, set by the reveal
            // sequencer) plus this tile's slot in the wipe.
            style={
              animate
                ? {
                    animationDelay: `calc(var(--pattern-delay, 0ms) + ${tileDelay(t)}ms)`,
                  }
                : undefined
            }
          >
            {shape}
          </g>
        )
      })}
    </g>
  )
}

/**
 * The mark. With `art` it becomes a window into the artwork: the logo's
 * silhouette masks the same gradient image the tiles crop from, so the mark is
 * literally made of the composition it sits on — one rendering for both
 * themes. Without `art` it falls back to the flat per-theme logo files.
 */
export function BrandMark({
  cx,
  cy,
  height,
  art,
  className,
  style,
}: {
  cx: number
  cy: number
  height: number
  art?: string
  className?: string
  style?: React.CSSProperties
}) {
  // useId emits ":r0:" — colons break url(#…) references, so strip to a
  // url-safe token.
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const width = height * MARK_RATIO
  const x = cx - width / 2
  const y = cy - height / 2

  if (art) {
    return (
      <g className={className} style={style}>
        <mask
          id={`mark-${uid}`}
          maskUnits="userSpaceOnUse"
          style={{ maskType: 'alpha' }}
        >
          {/* The mint-ink file shapes the mask in BOTH themes, deliberately:
              under the standard alpha interpretation ink color is irrelevant,
              and under a luminance fallback mint still passes ~66% — the navy
              light-theme file would luminance-mask to near-invisible. */}
          <image
            href="/logo_dark.svg"
            x={x}
            y={y}
            width={width}
            height={height}
          />
        </mask>
        <g mask={`url(#mark-${uid})`}>
          {/* slice-fit over the portrait mark box crops a centred vertical
              band out of the landscape artwork. */}
          <image
            href={art}
            x={x}
            y={y}
            width={width}
            height={height}
            preserveAspectRatio="xMidYMid slice"
          />
        </g>
      </g>
    )
  }

  return (
    <g className={className} style={style}>
      <image
        href="/logo_light.svg"
        x={x}
        y={y}
        width={width}
        height={height}
        opacity="0.88"
        className="dark:hidden"
      />
      <image
        href="/logo_dark.svg"
        x={x}
        y={y}
        width={width}
        height={height}
        opacity="0.82"
        className="hidden dark:block"
      />
    </g>
  )
}
